import React, { useEffect } from 'react';
import InputControl from '../InputControl/InputControl';
import SelectOption, { Option } from '../SelectOption/SelectOption';
import CycleSettings from '../../enums/cycle-setting.enum';
import StepsInputTable from './StepsInputTable';
import { IRecipeStepEditable } from '../../types/recipes-types';
import { ChooseRecipe } from '../PostWeldSettings/components/ChooseRecipeModal';
import { ChoosePreHeatRecipe } from '../PreHeatSettings/components/ChoosePreHeatRecipeModal';
import CycleMeasurement from '../../enums/cycle-measurement';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';

export type CycleSettingsFormValues = {
  name?: string | null;
  mode: string | null;
  selectedRecipeId?: string | null;
  // PreHeat fields
  rate?: number | string;
  finalTemp?: number | string;
  soakUpper?: number | string;
  soakLower?: number | string;
  // PercentageTimer fields
  percentage_timer?: number | string;
  // PostWeld fields
  rateUpperTolerance?: number | string;
  rateLowerTolerance?: number | string;
  holdUpperTolerance?: number | string;
  holdLowerTolerance?: number | string;
  steps?: IRecipeStepEditable[];
};

type ComponentProps = {
  values: CycleSettingsFormValues;
  onChange: (values: CycleSettingsFormValues) => void;
  onModeChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (field: string) => void;
  contractTemperatureUnit?: CycleMeasurement;
  onRecipeSelect?: (recipeId: string) => void;
  errors?: {
    mode?: string;
    name?: string;
    rate?: string;
    finalTemp?: string;
    soakUpper?: string;
    soakLower?: string;
    percentage_timer?: string;
    rateUpperTolerance?: string;
    rateLowerTolerance?: string;
    holdUpperTolerance?: string;
    holdLowerTolerance?: string;
    steps?: string;
  };
  touched?: {
    mode?: boolean;
    name?: boolean;
    rate?: boolean;
    finalTemp?: boolean;
    soakUpper?: boolean;
    soakLower?: boolean;
    percentage_timer?: boolean;
    rateUpperTolerance?: boolean;
    rateLowerTolerance?: boolean;
    holdUpperTolerance?: boolean;
    holdLowerTolerance?: boolean;
    steps?: boolean;
  };
};

// A blank tolerance is accepted as "no tolerance" (0) at submit time, but
// leaving the field visibly empty reads as "not filled in yet" - so as soon
// as a mode is picked, any of its tolerance fields that aren't already set
// get seeded with 0 to display right away.
const TOLERANCE_FIELDS_BY_MODE: Partial<Record<string, (keyof CycleSettingsFormValues)[]>> = {
  [CycleSettings.PreHeat]: ['soakUpper', 'soakLower'],
  [CycleSettings.PostWeld]: ['rateUpperTolerance', 'rateLowerTolerance', 'holdUpperTolerance', 'holdLowerTolerance'],
};

const CycleSettingsInput: React.FC<ComponentProps> = ({
  values,
  onChange,
  onModeChange,
  onBlur,
  contractTemperatureUnit = CycleMeasurement.Celsius,
  onRecipeSelect,
  errors = {},
  touched = {},
}) => {
  const temperatureSymbol = CycleMeasurementSymbol[contractTemperatureUnit];

  const handleFieldChange = (field: string, value: any) => {
    onChange({
      ...values,
      [field]: value,
    });
  };

  const handleStepsChange = (steps: IRecipeStepEditable[]) => {
    onChange({
      ...values,
      steps,
    });
  };

  useEffect(() => {
    const toleranceFields = TOLERANCE_FIELDS_BY_MODE[values.mode ?? ''];

    if (!toleranceFields) {
      return;
    }

    const unsetFields = toleranceFields.filter((field) => values[field] === undefined || values[field] === null);

    if (!unsetFields.length) {
      return;
    }

    onChange({
      ...values,
      ...unsetFields.reduce((defaults, field) => ({ ...defaults, [field]: 0 }), {}),
    });
    // Only re-run when the mode itself changes - this seeds defaults once
    // per mode selection, it isn't meant to fight the user clearing a field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.mode]);

  const modeOptions: Option[] = [
    { id: '', value: 'None' },
    { id: CycleSettings.PostWeld, value: 'Post Weld Heat Treatment' },
    { id: CycleSettings.PreHeat, value: 'Pre Heat' },
    { id: CycleSettings.PercentageTimer, value: 'Percentage Timer' },
    { id: CycleSettings.Recorder, value: 'Recorder' },
  ];

  const selectedModeOption = values.mode
    ? {
        id: values.mode,
        value:
          values.mode === CycleSettings.PostWeld
            ? 'Post Weld Heat Treatment'
            : values.mode === CycleSettings.PreHeat
            ? 'Pre Heat'
            : values.mode === CycleSettings.PercentageTimer
            ? 'Percentage Timer'
            : values.mode === CycleSettings.Recorder
            ? 'Recorder'
            : 'None',
      }
    : null;

  return (
    <div className="c-cycle-settings-input">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="text-xl font-semibold text-black dark:text-white">Cycle Settings (Optional)</h3>
        </div>

        <div className="p-7">
          <div className="mb-6">
            <SelectOption
              name="cycleSettings.mode"
              onChange={onModeChange}
              options={modeOptions}
              selectedOption={selectedModeOption}
              title="Cycle Mode"
              placeholder="Choose a cycle type or select None to clear"
            />
          </div>

          {values.mode && (
            <div className="mt-6">
              {/* PreHeat Settings */}
              {values.mode === CycleSettings.PreHeat && (
                <div className="space-y-4">
                  <h4 className="mb-4 text-lg font-medium text-black dark:text-white">Pre-Heat Configuration</h4>

                  {/* Recipe Selection */}
                  <div className="mb-4 flex justify-end">
                    {onRecipeSelect && <ChoosePreHeatRecipe onSelect={onRecipeSelect} />}
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <InputControl
                      type="number"
                      name="preheat_rate"
                      label={`Heating Rate (${temperatureSymbol}/h)`}
                      placeholder="Enter heating rate"
                      value={values?.rate ?? ''}
                      customOnChange={(e) =>
                        handleFieldChange('rate', e.target.value ? parseFloat(e.target.value) : '')
                      }
                      onBlur={() => onBlur?.('cycleSettings.rate')}
                      showError={touched.rate && !!errors.rate}
                      errorText={errors.rate}
                    />
                    <InputControl
                      type="number"
                      name="preheat_finalTemp"
                      label={`Final Temperature (${temperatureSymbol})`}
                      placeholder="Enter final temperature"
                      value={values?.finalTemp ?? ''}
                      customOnChange={(e) =>
                        handleFieldChange('finalTemp', e.target.value ? parseFloat(e.target.value) : '')
                      }
                      onBlur={() => onBlur?.('cycleSettings.finalTemp')}
                      showError={touched.finalTemp && !!errors.finalTemp}
                      errorText={errors.finalTemp}
                    />
                    <InputControl
                      type="number"
                      name="preheat_soakUpper"
                      label={`Upper Soak Tolerance (${temperatureSymbol})`}
                      placeholder="Enter upper soak tolerance"
                      value={values?.soakUpper ?? ''}
                      customOnChange={(e) =>
                        handleFieldChange('soakUpper', e.target.value ? parseFloat(e.target.value) : '')
                      }
                      onBlur={() => onBlur?.('cycleSettings.soakUpper')}
                      showError={touched.soakUpper && !!errors.soakUpper}
                      errorText={errors.soakUpper}
                    />
                    <InputControl
                      type="number"
                      name="preheat_soakLower"
                      label={`Lower Soak Tolerance (${temperatureSymbol})`}
                      placeholder="Enter lower soak tolerance"
                      value={values?.soakLower ?? ''}
                      customOnChange={(e) =>
                        handleFieldChange('soakLower', e.target.value ? parseFloat(e.target.value) : '')
                      }
                      onBlur={() => onBlur?.('cycleSettings.soakLower')}
                      showError={touched.soakLower && !!errors.soakLower}
                      errorText={errors.soakLower}
                    />
                  </div>
                </div>
              )}

              {/* Percentage Timer Settings */}
              {values.mode === CycleSettings.PercentageTimer && (
                <div className="space-y-4">
                  <h4 className="mb-4 text-lg font-medium text-black dark:text-white">
                    Percentage Timer Configuration
                  </h4>
                  <div className="w-full md:w-1/2">
                    <InputControl
                      type="number"
                      name="percentage_timer"
                      label="Percentage Timer (%)"
                      placeholder="Enter percentage (0-100)"
                      value={values?.percentage_timer ?? ''}
                      customOnChange={(e) =>
                        handleFieldChange('percentage_timer', e.target.value ? parseFloat(e.target.value) : '')
                      }
                      onBlur={() => onBlur?.('cycleSettings.percentage_timer')}
                      showError={touched.percentage_timer && !!errors.percentage_timer}
                      errorText={errors.percentage_timer}
                    />
                  </div>
                </div>
              )}

              {/* Post Weld Settings */}
              {values.mode === CycleSettings.PostWeld && (
                <div className="space-y-6">
                  <h4 className="mb-4 text-lg font-medium text-black dark:text-white">
                    Post Weld Heat Treatment Configuration
                  </h4>

                  {/* Recipe Selection */}
                  <div className="mb-4 flex justify-end">
                    {onRecipeSelect && <ChooseRecipe onSelect={onRecipeSelect} />}
                  </div>

                  <InputControl
                    type="text"
                    name="postweld_name"
                    label="Recipe Name"
                    placeholder="Enter recipe name"
                    value={values?.name || ''}
                    customOnChange={(e) => handleFieldChange('name', e.target.value)}
                    onBlur={() => onBlur?.('cycleSettings.name')}
                    showError={touched.name && !!errors.name}
                    errorText={errors.name}
                  />

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <InputControl
                      type="number"
                      name="postweld_rateUpperTolerance"
                      label={`Upper Ramp Tolerance (${temperatureSymbol}/h)`}
                      placeholder="Enter upper ramp tolerance"
                      value={values?.rateUpperTolerance ?? ''}
                      customOnChange={(e) =>
                        handleFieldChange('rateUpperTolerance', e.target.value ? parseFloat(e.target.value) : '')
                      }
                      onBlur={() => onBlur?.('cycleSettings.rateUpperTolerance')}
                      showError={touched.rateUpperTolerance && !!errors.rateUpperTolerance}
                      errorText={errors.rateUpperTolerance}
                    />
                    <InputControl
                      type="number"
                      name="postweld_rateLowerTolerance"
                      label={`Lower Ramp Tolerance (${temperatureSymbol}/h)`}
                      placeholder="Enter lower ramp tolerance"
                      value={values?.rateLowerTolerance ?? ''}
                      customOnChange={(e) =>
                        handleFieldChange('rateLowerTolerance', e.target.value ? parseFloat(e.target.value) : '')
                      }
                      onBlur={() => onBlur?.('cycleSettings.rateLowerTolerance')}
                      showError={touched.rateLowerTolerance && !!errors.rateLowerTolerance}
                      errorText={errors.rateLowerTolerance}
                    />
                    <InputControl
                      type="number"
                      name="postweld_holdUpperTolerance"
                      label={`Upper Hold Tolerance (${temperatureSymbol})`}
                      placeholder="Enter upper hold tolerance"
                      value={values?.holdUpperTolerance ?? ''}
                      customOnChange={(e) =>
                        handleFieldChange('holdUpperTolerance', e.target.value ? parseFloat(e.target.value) : '')
                      }
                      onBlur={() => onBlur?.('cycleSettings.holdUpperTolerance')}
                      showError={touched.holdUpperTolerance && !!errors.holdUpperTolerance}
                      errorText={errors.holdUpperTolerance}
                    />
                    <InputControl
                      type="number"
                      name="postweld_holdLowerTolerance"
                      label={`Lower Hold Tolerance (${temperatureSymbol})`}
                      placeholder="Enter lower hold tolerance"
                      value={values?.holdLowerTolerance ?? ''}
                      customOnChange={(e) =>
                        handleFieldChange('holdLowerTolerance', e.target.value ? parseFloat(e.target.value) : '')
                      }
                      onBlur={() => onBlur?.('cycleSettings.holdLowerTolerance')}
                      showError={touched.holdLowerTolerance && !!errors.holdLowerTolerance}
                      errorText={errors.holdLowerTolerance}
                    />
                  </div>

                  <div className="mt-6">
                    <StepsInputTable
                      steps={values?.steps || []}
                      onChange={handleStepsChange}
                      contractTemperatureUnit={contractTemperatureUnit}
                    />
                  </div>
                </div>
              )}

              {/* Recorder Settings */}
              {values.mode === CycleSettings.Recorder && <div className="space-y-4"></div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CycleSettingsInput;
